import { updateFpAppV4Data } from "@/redux/fpAppV4Slice";
import { liveEditor } from "@/utils/liveEditor";
import store from "../../../redux/store";
import { setProductTimeout } from "@/utils/productTimers";
import { flushPendingSaves } from "./saveWorkspaceContent";

/**
 * Hold the report still while the server writes a query's answer into it.
 *
 * The server announces the write (LOCK_WORKSPACE_EDITOR), sleeps a fixed
 * grace window (1.5 s in `get_answer.py`), then reads the report, inserts
 * the answer and writes the result back. Two things follow, and the lock
 * does both:
 *
 *  - It holds for the whole window, `holdMs`. A keystroke inside it lands in
 *    a report the server is about to overwrite with its own read-modify-write.
 *    Nothing on the client signals the server's read, so the hold is the
 *    server's own duration, on the product clock.
 *  - It sends the edit the autosave is still holding at once, rather than
 *    when its debounce fires, so that edit reaches the server inside the
 *    window instead of after the server has read the report (#12187).
 *
 * The release does not wait on the save: once the window has passed the
 * server has read the report, so holding longer protects nothing, and a save
 * that never answers cannot leave the report locked.
 */
export default async function lockEditor(holdMs: number = 1000) {
  // If we're in the matrix, this code expects `window.fp` to exist, which is not set for the matrix,
  // causing an exception and thus the query results will not propagate to the frontend. This fixes that.
  if (!window.fp) return;
  // liveEditor, twice: the report can unmount during the wait below, and
  // tiptap v3 throws on a destroyed editor's commands. Throwing between the
  // two dispatches left `editorDisabled` stuck at true, i.e. a report greyed
  // out and unclickable, which is why the reset is in a finally (#11904).
  const editor = liveEditor(window.fp.editor);
  if (!editor) return;
  const editorWrapper = document.getElementById("editor-wrapper");
  const editorFocusPositon = editor.state.selection.$head.pos;

  if (editorWrapper) {
    let scrollTop = editorWrapper?.scrollTop;
    store.dispatch(updateFpAppV4Data({ editorDisabled: true }));
    try {
      flushPendingSaves().catch((error: unknown) => {
        console.error("[lockEditor] Error sending the held edit:", error);
      });
      await new Promise<void>((resolve) => {
        setProductTimeout(resolve, holdMs);
      });
      liveEditor(window.fp.editor)?.commands.focus(editorFocusPositon);
    } finally {
      store.dispatch(updateFpAppV4Data({ editorDisabled: false }));
    }
    editorWrapper.scrollTop = scrollTop || 0;
  }
}
